import type {
  FimSubjectMatch,
  FimSubjectsPageFilters,
  FimSubjectSummary,
  FimSubjectSummaryData,
  SourceName,
} from 'features/fim'

import { FACILITY_MODEL_COLLATOR, type FacilityModelColumnKey } from './columns'

/**
 * One compact, search-ready row of the in-memory catalog index. Deliberately
 * NOT the summary object: ~200k summaries at full fidelity would hold well
 * over the memory budget, so rows keep only what search/filter/sort and the
 * table cells actually read, plus one precomputed lowercase haystack so
 * as-you-type filtering never re-lowercases the corpus.
 */
export interface SubjectIndexRow {
  uid: string
  entryId: string
  name: string
  type: string | null
  kind: string
  status: 'review' | 'proposed'
  subsystem: string | null
  sources: SourceName[]
  confidence: number
  validation: string
  needsReview: boolean
  reviewCategory: string | null
  aliasValues: string[]
  aliasNorms: string[]
  /** name + type + alias values + alias norms, newline-joined, lowercased. */
  hay: string
}

/** The index stream's manifest line: always first, and the completeness
 * contract for everything after it. */
export interface SubjectsIndexManifest {
  run_id: string
  count: number
}

/** One row line of the index stream — flat, and only what the index keeps. */
export interface SubjectsIndexStreamRow {
  uid: string
  entry_id: string
  status: 'review' | 'proposed'
  alias_values: string[]
  alias_norms: string[]
  name: string
  kind: string
  type: string | null
  subsystem_id: string | null
  existence_confidence: number
  source_set: SourceName[]
  review_reason: string | null
  review_category: string | null
  validation_status: string | null
}

const EMPTY_STRINGS: string[] = []

export function buildStreamedIndexRow(
  wire: SubjectsIndexStreamRow
): SubjectIndexRow {
  const aliasValues = wire.alias_values ?? EMPTY_STRINGS
  const aliasNorms = wire.alias_norms ?? EMPTY_STRINGS
  const name = wire.name
  const type = wire.type ?? null
  return {
    uid: wire.uid,
    entryId: wire.entry_id,
    name,
    type,
    kind: wire.kind,
    status: wire.status,
    subsystem: wire.subsystem_id ?? null,
    sources: wire.source_set ?? [],
    confidence: wire.existence_confidence,
    validation: wire.validation_status ?? 'unvalidated',
    needsReview:
      wire.status === 'review' ||
      (wire.review_reason !== null && wire.review_reason !== undefined),
    reviewCategory: wire.review_category ?? null,
    aliasValues,
    aliasNorms,
    hay: [name, type ?? '', ...aliasValues, ...aliasNorms]
      .join('\n')
      .toLowerCase(),
  }
}

/**
 * The server's relevance tiers, reproduced for in-memory ranking: name-exact
 * (0) > name-prefix (1) > name-substring (2) > type (3) > alias-value (4) >
 * alias-norm (5). The best tier any term achieves ranks the row; the
 * (field, text) behind that tier becomes the row's match info.
 */
function rowRelevance(
  row: SubjectIndexRow,
  termsLower: readonly string[]
): { tier: number; match: FimSubjectMatch } {
  const nameLower = row.name.toLowerCase()
  const typeLower = row.type?.toLowerCase() ?? ''
  let best: { tier: number; match: FimSubjectMatch } | null = null
  const consider = (tier: number, match: FimSubjectMatch) => {
    if (!best || tier < best.tier) best = { tier, match }
  }
  for (const term of termsLower) {
    if (nameLower === term) {
      consider(0, { source: 'name', text: row.name })
    } else if (nameLower.startsWith(term)) {
      consider(1, { source: 'name', text: row.name })
    } else if (nameLower.includes(term)) {
      consider(2, { source: 'name', text: row.name })
    } else if (typeLower.includes(term)) {
      consider(3, { source: 'type', text: row.type ?? '' })
    } else {
      const valueAt = row.aliasValues.findIndex((value) =>
        value.toLowerCase().includes(term)
      )
      if (valueAt >= 0) {
        consider(4, { source: 'alias_value', text: row.aliasValues[valueAt] })
        continue
      }
      const normAt = row.aliasNorms.findIndex((norm) =>
        norm.toLowerCase().includes(term)
      )
      if (normAt >= 0) {
        // The norm matched; show the alias VALUE it belongs to, like the API.
        consider(5, {
          source: 'alias_norm',
          text: row.aliasValues[normAt] ?? row.aliasNorms[normAt],
        })
      }
    }
  }
  return best ?? { tier: 6, match: { source: 'name', text: row.name } }
}

const rowSortValue = (
  row: SubjectIndexRow,
  key: FacilityModelColumnKey
): string | number => {
  switch (key) {
    case 'name':
      return row.name
    case 'kind':
      return row.kind
    case 'type':
      return row.type ?? ''
    case 'subsystem':
      return row.subsystem ?? ''
    case 'confidence':
      return row.confidence
    case 'sources':
      return row.sources.length
    case 'validation':
      return row.validation
    default:
      return row.name
  }
}

/** Sort keys the index can order globally (the rest are instance-side). */
export const INDEX_SORT_KEYS: ReadonlySet<FacilityModelColumnKey> = new Set([
  'name',
  'kind',
  'type',
  'subsystem',
  'confidence',
  'sources',
  'validation',
])

export interface SubjectsIndexQuery {
  filters: FimSubjectsPageFilters
  sortKey?: FacilityModelColumnKey
  sortDir?: 'asc' | 'desc'
  offset: number
  limit: number
}

export interface SubjectsIndexPage {
  total: number
  /** The requested page, materialized as summary records (match included). */
  subjects: FimSubjectSummary[]
}

const byName = (left: SubjectIndexRow, right: SubjectIndexRow) =>
  FACILITY_MODEL_COLLATOR.compare(left.name, right.name)

/**
 * The server-paged read's semantics over the local index: same ANDed
 * case-insensitive terms over the same haystack, same filters, relevance
 * ranking under search unless a column sort overrides, name ascending
 * otherwise.
 */
export function queryIndex(
  rows: readonly SubjectIndexRow[],
  query: SubjectsIndexQuery
): SubjectsIndexPage {
  const { filters, sortKey, sortDir, offset, limit } = query
  const termsLower = (filters.q ?? [])
    .map((term) => term.trim().toLowerCase())
    .filter(Boolean)
  const uidSet = filters.uids ? new Set(filters.uids) : undefined

  const matched: SubjectIndexRow[] = []
  for (const row of rows) {
    if (uidSet && !uidSet.has(row.uid)) continue
    if (filters.kind && row.kind !== filters.kind) continue
    if (filters.type && row.type !== filters.type) continue
    if (filters.subsystemId && row.subsystem !== filters.subsystemId) continue
    if (filters.validationStatus && row.validation !== filters.validationStatus)
      continue
    if (filters.needsReview && !row.needsReview) continue
    if (
      filters.minSources !== undefined &&
      row.sources.length < filters.minSources
    )
      continue
    if (
      filters.minConfidence !== undefined &&
      row.confidence < filters.minConfidence
    )
      continue
    if (termsLower.length > 0) {
      let all = true
      for (const term of termsLower) {
        if (!row.hay.includes(term)) {
          all = false
          break
        }
      }
      if (!all) continue
    }
    matched.push(row)
  }

  const searching = termsLower.length > 0
  const matchByUid = new Map<string, FimSubjectMatch>()
  const indexSort =
    sortKey && INDEX_SORT_KEYS.has(sortKey) ? sortKey : undefined
  if (searching) {
    const tierByUid = new Map<string, number>()
    for (const row of matched) {
      const { tier, match } = rowRelevance(row, termsLower)
      tierByUid.set(row.uid, tier)
      matchByUid.set(row.uid, match)
    }
    if (!indexSort) {
      matched.sort(
        (left, right) =>
          (tierByUid.get(left.uid) ?? 6) - (tierByUid.get(right.uid) ?? 6) ||
          byName(left, right)
      )
    }
  }
  if (indexSort) {
    const direction = sortDir === 'desc' ? -1 : 1
    matched.sort((left, right) => {
      const a = rowSortValue(left, indexSort)
      const b = rowSortValue(right, indexSort)
      const compared =
        typeof a === 'number' && typeof b === 'number'
          ? a - b
          : FACILITY_MODEL_COLLATOR.compare(String(a), String(b))
      return direction * compared || byName(left, right)
    })
  } else if (!searching) {
    matched.sort(byName)
  }

  return {
    total: matched.length,
    subjects: matched
      .slice(offset, offset + limit)
      .map((row) => indexRowToSummary(row, matchByUid.get(row.uid))),
  }
}

/**
 * A table-render-sufficient summary for one index row. Alias objects are
 * deliberately empty — no catalog cell reads them, and rebuilding them would
 * mean carrying kind/precision for 200k rows for nothing.
 */
export function indexRowToSummary(
  row: SubjectIndexRow,
  match?: FimSubjectMatch
): FimSubjectSummary {
  return {
    id: row.entryId,
    subjectUid: row.uid,
    status: row.status,
    datapoints: EMPTY_STRINGS,
    ...(match ? { match } : {}),
    data: {
      name: row.name,
      kind: row.kind as FimSubjectSummaryData['kind'],
      type: row.type,
      subsystem_id: row.subsystem,
      existence_confidence: row.confidence,
      source_set: row.sources,
      review_reason: row.needsReview && row.status !== 'review' ? '' : null,
      review_category: row.reviewCategory,
      validation_status:
        row.validation as FimSubjectSummaryData['validation_status'],
      aliases: [],
    },
  }
}
